import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Bold,
  Italic,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo2,
  Redo2,
  Code,
  Minus,
  Pilcrow,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ToolbarButton = ({ onClick, active, disabled, title, testid, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testid}
    className={`inline-flex h-8 w-8 items-center justify-center rounded-md border text-[12px] transition-all ${
      active
        ? "border-[#e85a1c] bg-[#e85a1c] text-white"
        : "border-black/10 bg-white text-[#1c1c1e] hover:border-[#e85a1c] hover:text-[#e85a1c]"
    } disabled:opacity-40`}
  >
    {children}
  </button>
);

/**
 * TipTap rich-text editor.
 * Outputs HTML via onChange(html).
 */
const RichTextEditor = ({ value, onChange, placeholder = "Start writing your article…", token }) => {
  const fileInputRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noreferrer noopener", target: "_blank" },
      }),
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "blog-prose min-h-[260px] max-w-none rounded-b-md border border-t-0 border-black/10 bg-white px-4 py-3 text-sm leading-[1.7] text-[#1c1c1e] focus:outline-none",
        "data-testid": "rte-editor",
      },
    },
    onUpdate: ({ editor: e }) => {
      onChange?.(e.getHTML());
    },
  });

  if (!editor) return null;

  const insertImageFromUpload = async (file) => {
    if (!file || !token) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("JPG, PNG or WEBP only");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("files", file);
      const res = await axios.post(`${API}/admin/media/upload`, fd, {
        headers: { "x-admin-token": token },
      });
      const added = res.data?.added?.[0];
      const url = added?.urls?.large || added?.url;
      if (!url) {
        toast.error("Upload failed");
        return;
      }
      const absolute = url.startsWith("http") ? url : `${BACKEND}${url}`;
      editor.chain().focus().setImage({ src: absolute, alt: added.original_name }).run();
      toast.success("Image inserted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const promptImage = () => {
    fileInputRef.current?.click();
  };

  const promptLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave blank to remove)", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="overflow-hidden rounded-md" data-testid="rich-text-editor">
      {/* Hidden file input for inline image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => insertImageFromUpload(e.target.files?.[0])}
        data-testid="rte-image-input"
      />
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1.5 rounded-t-md border border-black/10 bg-[#f7f7f5] p-2">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          title="Bold"
          testid="rte-bold"
        >
          <Bold size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          title="Italic"
          testid="rte-italic"
        >
          <Italic size={13} />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-black/10" />
        <ToolbarButton
          onClick={() => editor.chain().focus().setParagraph().run()}
          active={editor.isActive("paragraph")}
          title="Paragraph"
          testid="rte-paragraph"
        >
          <Pilcrow size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive("heading", { level: 2 })}
          title="Heading 2"
          testid="rte-h2"
        >
          <Heading2 size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive("heading", { level: 3 })}
          title="Heading 3"
          testid="rte-h3"
        >
          <Heading3 size={13} />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-black/10" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          title="Bullet list"
          testid="rte-ul"
        >
          <List size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          title="Ordered list"
          testid="rte-ol"
        >
          <ListOrdered size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive("blockquote")}
          title="Quote"
          testid="rte-quote"
        >
          <Quote size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          active={editor.isActive("codeBlock")}
          title="Code block"
          testid="rte-code"
        >
          <Code size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Divider"
          testid="rte-hr"
        >
          <Minus size={13} />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-black/10" />
        <ToolbarButton onClick={promptLink} active={editor.isActive("link")} title="Link" testid="rte-link">
          <LinkIcon size={13} />
        </ToolbarButton>
        <ToolbarButton onClick={promptImage} title="Insert image" testid="rte-image">
          <ImageIcon size={13} />
        </ToolbarButton>
        <span className="mx-1 h-5 w-px bg-black/10" />
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
          testid="rte-undo"
        >
          <Undo2 size={13} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
          testid="rte-redo"
        >
          <Redo2 size={13} />
        </ToolbarButton>
      </div>

      {/* Editor */}
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
